import { API_BASE } from './env'
import { getLocale, t } from './i18n'
import { streamSse, type SseEvent } from './sse'
import {
  authHeaders,
  clearSession,
  getAccessToken,
  getRefreshToken,
  loadPersistedRefresh,
  persistSession,
} from './session'

export class ApiError extends Error {
  status: number
  code?: string
  constructor(message: string, status: number, code?: string) {
    super(message)
    this.status = status
    this.code = code
  }
}

export function getStoredToken(): string | null {
  return getAccessToken()
}

export function setStoredToken(token: string): void {
  localStorage.setItem('juniverso_access', token)
}

export function clearStoredToken(): void {
  void logoutRemote()
}

async function refreshAccess(): Promise<string | null> {
  if (refreshInFlight) return refreshInFlight
  refreshInFlight = doRefresh().finally(() => {
    refreshInFlight = null
  })
  return refreshInFlight
}

let refreshInFlight: Promise<string | null> | null = null

async function doRefresh(): Promise<string | null> {
  const refresh = getRefreshToken()
  if (!refresh || refresh === getAccessToken()) return null
  const response = await fetch(`${API_BASE}/auth/refresh`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ refresh_token: refresh }),
  })
  if (!response.ok) return null
  const body = (await response.json()) as { access_token: string; refresh_token: string }
  await persistSession(body.access_token, body.refresh_token)
  return body.access_token
}

export async function logoutRemote(): Promise<void> {
  const refresh = getRefreshToken()
  if (refresh && refresh !== getAccessToken()) {
    try {
      await fetch(`${API_BASE}/auth/logout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refresh_token: refresh }),
      })
    } catch {
      // Local clear still happens.
    }
  }
  await clearSession()
}

export async function requestOtp(email: string): Promise<void> {
  const response = await fetch(`${API_BASE}/auth/otp/request`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, locale: getLocale() }),
  })
  if (!response.ok) throw await parseError(response)
}

export async function verifyOtp(email: string, code: string): Promise<string> {
  const response = await fetch(`${API_BASE}/auth/otp/verify`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, code, locale: getLocale() }),
  })
  if (!response.ok) throw await parseError(response)
  const body = (await response.json()) as { access_token: string; refresh_token: string }
  await persistSession(body.access_token, body.refresh_token)
  return body.access_token
}

export async function restoreSession(): Promise<string | null> {
  await loadPersistedRefresh()
  const refreshed = await refreshAccess()
  if (refreshed) return refreshed
  return getAccessToken()
}

async function parseError(response: Response): Promise<ApiError> {
  const body = await response.json().catch(() => null)
  const message = body?.error?.message ?? t('Помилка {status}', { status: response.status })
  const code = body?.error?.code
  return new ApiError(message, response.status, code)
}

/**
 * Lightweight JSON fetch, mirrors the cabinet helper. Used for the login probe
 * (`GET /rules`) and any future JSON calls.
 */
export async function api<T>(path: string, token: string, init?: RequestInit): Promise<T> {
  async function once(auth: string): Promise<Response> {
    return fetch(`${API_BASE}${path}`, {
      ...init,
      headers: {
        'Content-Type': 'application/json',
        ...authHeaders(auth),
        ...(init?.headers ?? {}),
      },
    })
  }
  let response = await once(token)
  if (response.status === 401) {
    const next = await refreshAccess()
    if (next) response = await once(next)
  }
  if (response.status === 401) {
    clearStoredToken()
    throw new ApiError(t('Сесію завершено. Увійдіть знову.'), 401)
  }
  if (!response.ok) throw await parseError(response)
  if (response.status === 204) return undefined as T
  return (await response.json()) as T
}

export function postIgnore(params: {
  token: string
  ruleId: string
  reviewId?: string | null
}): Promise<{ rule_id: string; ignore_count: number; queued: boolean }> {
  return api('/ignores', params.token, {
    method: 'POST',
    body: JSON.stringify({
      rule_id: params.ruleId,
      review_id: params.reviewId ?? null,
    }),
  })
}

export interface ContractTypeOption {
  value: string
  ruleCount: number
  unapprovedCount: number
  excludedCount: number
}

export function contractTypesFromRules(
  rules: {
    contract_type: string | null
    approved_at?: string | null
    exclude_from_review?: boolean
  }[],
): ContractTypeOption[] {
  const counts = new Map<string, { ruleCount: number; unapprovedCount: number; excludedCount: number }>()
  for (const rule of rules) {
    const value = (rule.contract_type ?? '').trim()
    if (!value) continue
    const current = counts.get(value) ?? { ruleCount: 0, unapprovedCount: 0, excludedCount: 0 }
    if (rule.approved_at) {
      if (rule.exclude_from_review) current.excludedCount += 1
      else current.ruleCount += 1
    } else {
      current.unapprovedCount += 1
    }
    counts.set(value, current)
  }
  return [...counts.entries()]
    .map(([value, countsForType]) => ({ value, ...countsForType }))
    .sort((a, b) => a.value.localeCompare(b.value))
}

/** Word can only review a type that has at least one agreed rule. */
export function typesWithApprovedRules(types: ContractTypeOption[]): ContractTypeOption[] {
  return types.filter((t) => t.ruleCount > 0)
}

/**
 * Contract types available for review, derived from the active rules. There is
 * no separate types table — the playbook itself is the source of truth.
 * `ruleCount` is approved rules Word will apply; drafts stay in `unapprovedCount`.
 * Approved rules excluded from comparison are omitted from `ruleCount` and counted in `excludedCount`.
 */
export async function fetchContractTypes(token: string): Promise<ContractTypeOption[]> {
  const rules = await api<{
    contract_type: string | null
    approved_at?: string | null
    exclude_from_review?: boolean
  }[]>(
    '/rules?status=active',
    token,
  )
  return contractTypesFromRules(rules)
}

/**
 * Open the /review SSE stream. `onEvent` receives already-parsed events; the
 * caller does the JSON.parse per event because different events have different
 * payload shapes.
 */
export async function streamReview(params: {
  token: string
  ooxml: string
  filename?: string
  contract_type?: string
  signal?: AbortSignal
  onEvent: (evt: SseEvent) => void
}): Promise<void> {
  const body = {
    ooxml: params.ooxml,
    ...(params.filename ? { filename: params.filename } : {}),
    ...(params.contract_type ? { contract_type: params.contract_type } : {}),
  }
  try {
    await streamSse({
      url: `${API_BASE}/review`,
      headers: authHeaders(params.token),
      body,
      signal: params.signal,
      onEvent: params.onEvent,
    })
  } catch (err) {
    const status = (err as { status?: number }).status
    if (status !== 401) throw err
    const next = await refreshAccess()
    if (!next) throw err
    await streamSse({
      url: `${API_BASE}/review`,
      headers: authHeaders(next),
      body,
      signal: params.signal,
      onEvent: params.onEvent,
    })
  }
}

export function postRevise(params: {
  token: string
  clauseText: string
  ruleId?: string | null
  justification?: string | null
  preferredPosition?: string | null
  modelLanguage?: string | null
  modelLanguageOptions?: string[] | null
}): Promise<{ revised_text: string; source: string }> {
  return api('/review/revise', params.token, {
    method: 'POST',
    body: JSON.stringify({
      clause_text: params.clauseText,
      rule_id: params.ruleId ?? null,
      justification: params.justification ?? null,
      preferred_position: params.preferredPosition ?? null,
      model_language: params.modelLanguage ?? null,
      model_language_options: params.modelLanguageOptions ?? [],
    }),
  })
}

export type AssistantAction =
  | 'ask'
  | 'summarize_redlines'
  | 'check_defined_terms'
  | 'extract_key_terms'
  | 'check_placeholders'
  | 'translate_redlines'

export function postAssistant(params: {
  token: string
  action: AssistantAction
  question?: string
  documentText: string
  findings?: unknown[]
}): Promise<{ answer: string; action: AssistantAction }> {
  return api('/assistant', params.token, {
    method: 'POST',
    body: JSON.stringify({
      action: params.action,
      question: params.question ?? null,
      document_text: params.documentText,
      findings: params.findings ?? [],
    }),
  })
}

export interface ClauseHit {
  source: string
  rule_id: string
  title: string
  text: string
  contract_type: string
  kind: string
}

export function searchClauses(params: {
  token: string
  q: string
  contractType?: string
}): Promise<{ hits: ClauseHit[] }> {
  const qs = new URLSearchParams({ q: params.q })
  if (params.contractType) qs.set('contract_type', params.contractType)
  return api(`/clauses/search?${qs.toString()}`, params.token)
}

export interface ArchiveHit {
  chunk_id: string
  document_id: string
  filename: string
  section_path: string | null
  snippet: string
  score: number
}

export function searchArchive(params: {
  token: string
  q: string
}): Promise<{ hits: ArchiveHit[]; degraded?: boolean }> {
  const qs = new URLSearchParams({ q: params.q, k: '10' })
  return api(`/archive/search?${qs.toString()}`, params.token)
}

export interface RequisitesField {
  key: string
  label: string
  value: string
}

export interface RequisitesCompany {
  id: string
  title: string
  legal_name: string
  extras?: { label: string; value: string }[]
  fields: RequisitesField[]
  formatted_text: string
  empty: boolean
}

export interface RequisitesResponse {
  legal_name: string
  extras?: { label: string; value: string }[]
  fields: RequisitesField[]
  formatted_text: string
  empty: boolean
  companies?: RequisitesCompany[]
}

export function getRequisites(token: string): Promise<RequisitesResponse> {
  return api('/tenant/requisites', token)
}

export function fetchCatalogRevision(token: string): Promise<{ revision: string }> {
  return api('/catalog/revision', token)
}

export function streamCatalog(params: {
  token: string
  signal?: AbortSignal
  onRevision: (revision: string) => void
}): Promise<void> {
  return streamSse({
    url: `${API_BASE}/catalog/stream`,
    method: 'GET',
    headers: authHeaders(params.token),
    signal: params.signal,
    onEvent: (evt) => {
      if (evt.event !== 'catalog') return
      try {
        const parsed = JSON.parse(evt.data) as { revision?: string }
        if (parsed.revision) params.onRevision(parsed.revision)
      } catch {
        // Ignore a malformed frame; the next event or catch-up GET will recover.
      }
    },
  })
}


export async function postFeedback(token: string, reviewId: string, body: {
  rule_id?: string | null; label: 'correct' | 'false_positive' | 'missed_risk'; comment?: string
}): Promise<void> {
  const response = await fetch(`${API_BASE}/history/${encodeURIComponent(reviewId)}/feedback`, {
    method: 'POST', headers: {...authHeaders(token), 'Content-Type': 'application/json'},
    body: JSON.stringify(body),
  })
  if (!response.ok) throw new ApiError(t('Не вдалося зберегти відгук'), response.status)
}
